import type React from 'react'

import type { KeyIconsType } from 'components/uiKit/Icon'
import type { MixedValue } from 'components/uiKit/KitUtils'

import type { IPlacement } from './Dropdown/types'

export type ValueType = string | number | boolean | null

export interface IKitControlProps<V = unknown, M = unknown> extends KitAccessState {
  /**
   * String for QA auto tests.
   */
  name: string

  /**
   * The current value of the control.
   */
  value?: V | MixedValue

  /**
   * The default value of the control.
   */
  defaultValue?: V | MixedValue

  /**
   * A callback function that is called when the value of the control changes.
   * @param value The new value of the control.
   */
  onChange?: (value: V, meta?: M) => void

  /**
   * The size of the control.
   */
  size?: KitSize

  /**
   * A callback function that is called when the control loses focus.
   */
  onBlur?: <E>(event?: React.FocusEvent<E>) => void

  /**
   * A callback function that is called when the control gains focus.
   */
  onFocus?: <E>(event?: React.FocusEvent<E>) => void

  /**
   * Show error state.
   */
  error?: boolean
}

export type KitAccessState = {
  /**
   * Specifies whether the control is disabled.
   */
  disabled?: boolean

  /**
   * Specifies whether the control is read-only.
   */
  readOnly?: boolean

  /**
   * Specifies whether the control is hidden.
   */
  hidden?: boolean
}

export interface IOption<V, LP extends ILabelProps = ILabelProps> {
  value: V
  label?: React.ReactNode | React.FC<LP>
  textLabel?: string
  disabled?: boolean
  hidden?: boolean
  testData?: string
  icon?: KeyIconsType
  tooltip?: string
  tooltipPlacement?: IPlacement
}

export interface ILabelProps {
  size?: KitSize
  disabled?: boolean
  error?: boolean
  type?: 'list' | 'root'
}

export type KitDimensions = Pick<
  React.CSSProperties,
  | 'width'
  | 'maxWidth'
  | 'minWidth'
  | 'height'
  | 'maxHeight'
  | 'minHeight'
  | 'flexBasis'
  | 'flexGrow'
  | 'flexShrink'
>

export type KitIndents = Pick<
  React.CSSProperties,
  | 'margin'
  | 'marginLeft'
  | 'marginTop'
  | 'marginRight'
  | 'marginBottom'
  | 'padding'
  | 'paddingLeft'
  | 'paddingTop'
  | 'paddingRight'
  | 'paddingBottom'
>

export enum KitSize {
  XS = 'xmini',
  S = 'mini',
  M = 'medium',
  L = 'large',
  XL = 'xlarge',
}

// can be extended by array, string etc.
export type RenderFunction<P> = (params: P) => React.ReactElement
